import { CheckIcon, CopyIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Progress,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  MdApartment, MdBed, MdRestaurant, MdPhone, MdBusinessCenter, MdBuild,
  MdPublic, MdComputer, MdDirectionsCar, MdLocalLaundryService, MdStorefront,
  MdFitnessCenter, MdContentCut, MdPrecisionManufacturing, MdCategory,
  MdCalculate, MdTrendingUp, MdFormatListNumbered,
} from 'react-icons/md';
import { useEffect, useMemo, useState } from 'react';
import depreciacionApi from 'api/depreciacion';
import { isSoloLectura } from 'utils/permissions';

interface CategoriaDepreciacion {
  Id: number;
  Categoria: string;
  Porcentaje: string | number;
  Anio: number;
  ActivosEnUso?: number;
}

const ANIO_ACTUAL = new Date().getFullYear();

// Ícono representativo por rubro, para que la tabla se escanee más rápido a simple vista.
const getIconoCategoria = (categoria: string) => {
  const c = categoria.toUpperCase();
  if (c.includes('EDIFICIO')) return MdApartment;
  if (c.includes('HABITACION')) return MdBed;
  if (c.includes('ALIMENTOS') || c.includes('BEBIDAS')) return MdRestaurant;
  if (c.includes('TELEFONO')) return MdPhone;
  if (c.includes('ADMON') || c.includes('ADMINISTRA')) return MdBusinessCenter;
  if (c.includes('MANTENIMIENTO')) return MdBuild;
  if (c.includes('AREAS PUBLICAS') || c.includes('ÁREAS PÚBLICAS')) return MdPublic;
  if (c.includes('COMPUTO') || c.includes('CÓMPUTO')) return MdComputer;
  if (c.includes('TRANSPORTE')) return MdDirectionsCar;
  if (c.includes('LAVANDERIA') || c.includes('LAVANDERÍA')) return MdLocalLaundryService;
  if (c.includes('BOUTIQUE')) return MdStorefront;
  if (c.includes('GIMNACIO') || c.includes('GIMNASIO')) return MdFitnessCenter;
  if (c.includes('BELLEZA') || c.includes('BELLESA') || c.includes('SALON')) return MdContentCut;
  if (c.includes('MAQUINARIA')) return MdPrecisionManufacturing;
  return MdCategory;
};

// Color por rango de porcentaje: ayuda a distinguir de un vistazo qué rubros
// se deprecian más rápido (equipo de cómputo, transporte) de los más lentos (edificio).
const getColorPorcentaje = (p: number): string => {
  if (p >= 25) return 'red';
  if (p >= 20) return 'orange';
  if (p >= 10) return 'blue';
  return 'gray';
};

const PORCENTAJE_ESCALA_MAX = 35; // referencia visual de la barra (el rubro más alto hoy es 30%)

export default function ConfigDepreciacion() {
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const soloLectura = isSoloLectura();

  const [anios, setAnios] = useState<number[]>([]);
  const [anioSeleccionado, setAnioSeleccionado] = useState<number>(ANIO_ACTUAL);
  const [tabla, setTabla] = useState<CategoriaDepreciacion[]>([]);
  const [cargando, setCargando] = useState(true);

  const { isOpen: isOpenDuplicar, onOpen: onOpenDuplicar, onClose: onCloseDuplicar } = useDisclosure();

  const [nuevaCategoria, setNuevaCategoria] = useState('');
  const [nuevoPorcentaje, setNuevoPorcentaje] = useState('');
  const [guardandoNueva, setGuardandoNueva] = useState(false);

  const [anioDestino, setAnioDestino] = useState<number>(ANIO_ACTUAL + 1);
  const [duplicando, setDuplicando] = useState(false);

  const stats = useMemo(() => {
    if (tabla.length === 0) return null;
    const porcentajes = tabla.map(f => Number(f.Porcentaje));
    const promedio = porcentajes.reduce((a, b) => a + b, 0) / porcentajes.length;
    const masAlto = tabla.reduce((max, f) => (Number(f.Porcentaje) > Number(max.Porcentaje) ? f : max), tabla[0]);
    const masBajo = tabla.reduce((min, f) => (Number(f.Porcentaje) < Number(min.Porcentaje) ? f : min), tabla[0]);
    return { promedio, masAlto, masBajo };
  }, [tabla]);

  const cargarAnios = async () => {
    const lista = await depreciacionApi.anios();
    setAnios(lista);
    if (lista.length > 0 && !lista.includes(anioSeleccionado)) {
      setAnioSeleccionado(lista[0]);
    }
    return lista;
  };

  const cargarTabla = async (anio: number) => {
    setCargando(true);
    try {
      const filas = await depreciacionApi.listado(anio);
      setTabla(filas);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarAnios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (anioSeleccionado) cargarTabla(anioSeleccionado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anioSeleccionado]);

  const handleEditarPorcentaje = async (fila: CategoriaDepreciacion, nuevoValor: string) => {
    const porcentaje = parseFloat(nuevoValor);
    if (isNaN(porcentaje) || String(porcentaje) === String(fila.Porcentaje)) return;
    try {
      await depreciacionApi.editar(fila.Id, { Categoria: fila.Categoria, Porcentaje: porcentaje });
      setTabla(prev => prev.map(f => (f.Id === fila.Id ? { ...f, Porcentaje: porcentaje } : f)));
      toast({ title: 'Porcentaje actualizado', status: 'success', duration: 2500 });
    } catch (error) {
      console.error(error);
      toast({ title: 'No se pudo actualizar', status: 'error', duration: 4000 });
      cargarTabla(anioSeleccionado);
    }
  };

  const handleEditarCategoria = async (fila: CategoriaDepreciacion, nuevoValor: string) => {
    const nombre = nuevoValor.trim().toUpperCase();
    if (!nombre || nombre === fila.Categoria) return;
    try {
      await depreciacionApi.editar(fila.Id, { Categoria: nombre, Porcentaje: Number(fila.Porcentaje) });
      setTabla(prev => prev.map(f => (f.Id === fila.Id ? { ...f, Categoria: nombre } : f)));
      toast({ title: 'Rubro renombrado', status: 'success', duration: 2500 });
    } catch (error: any) {
      toast({
        title: 'No se pudo renombrar',
        description: error?.response?.data?.error || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 4000,
      });
      cargarTabla(anioSeleccionado);
    }
  };

  const handleEliminar = async (fila: CategoriaDepreciacion) => {
    if (!window.confirm(`¿Eliminar el rubro "${fila.Categoria}"? Esta acción no se puede deshacer.`)) return;
    try {
      await depreciacionApi.eliminar(fila.Id);
      setTabla(prev => prev.filter(f => f.Id !== fila.Id));
      toast({ title: 'Categoría eliminada', status: 'success', duration: 2500 });
    } catch (error: any) {
      toast({
        title: 'No se pudo eliminar',
        description: error?.response?.data?.error || 'Ocurrió un error inesperado.',
        status: 'warning',
        duration: 5000,
      });
    }
  };

  const handleCrearCategoria = async () => {
    if (!nuevaCategoria.trim() || !nuevoPorcentaje) {
      toast({ title: 'Completa categoría y porcentaje', status: 'warning', duration: 3000 });
      return;
    }
    setGuardandoNueva(true);
    try {
      await depreciacionApi.crear({
        Categoria: nuevaCategoria.trim().toUpperCase(),
        Porcentaje: parseFloat(nuevoPorcentaje),
        Anio: anioSeleccionado,
      });
      toast({ title: 'Rubro agregado', status: 'success', duration: 2500 });
      setNuevaCategoria('');
      setNuevoPorcentaje('');
      cargarTabla(anioSeleccionado);
    } catch (error: any) {
      toast({
        title: 'No se pudo agregar',
        description: error?.response?.data?.error || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setGuardandoNueva(false);
    }
  };

  const handleDuplicar = async () => {
    setDuplicando(true);
    try {
      await depreciacionApi.duplicarAnio(anioSeleccionado, anioDestino);
      toast({
        title: `Tabla copiada a ${anioDestino}`,
        description: 'Ya puedes ajustar los porcentajes que hayan cambiado para ese año.',
        status: 'success',
        duration: 4000,
      });
      onCloseDuplicar();
      const lista = await cargarAnios();
      setAnioSeleccionado(anioDestino);
      if (!lista.includes(anioDestino)) setAnios([anioDestino, ...lista]);
    } catch (error: any) {
      toast({
        title: 'No se pudo duplicar',
        description: error?.response?.data?.error || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setDuplicando(false);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: 4, md: 6 }} pb={6}>
      <Flex justify="space-between" align={{ base: 'start', md: 'center' }} direction={{ base: 'column', md: 'row' }} mb={6} gap={3}>
        <Box>
          <Heading size="lg">Tabla de depreciación</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Porcentaje anual de depreciación por rubro de activo. Puedes llevar un catálogo distinto por año.
          </Text>
        </Box>
        {!soloLectura && (
          <HStack>
            <Button leftIcon={<CopyIcon />} variant="outline" onClick={onOpenDuplicar} isDisabled={!anioSeleccionado}>
              Duplicar a nuevo año
            </Button>
          </HStack>
        )}
      </Flex>

      {stats && (
        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mb={5}>
          <Card variant="outline" bg={cardBg} borderColor={borderColor}>
            <CardBody py={4}>
              <HStack spacing={3}>
                <Flex align="center" justify="center" w="36px" h="36px" borderRadius="lg" bg="blue.50" _dark={{ bg: 'whiteAlpha.100' }}>
                  <Icon as={MdFormatListNumbered} color="blue.500" w={5} h={5} />
                </Flex>
                <Box>
                  <Text fontSize="xs" color="gray.500">Rubros capturados</Text>
                  <Text fontSize="lg" fontWeight="700">{tabla.length}</Text>
                </Box>
              </HStack>
            </CardBody>
          </Card>
          <Card variant="outline" bg={cardBg} borderColor={borderColor}>
            <CardBody py={4}>
              <HStack spacing={3}>
                <Flex align="center" justify="center" w="36px" h="36px" borderRadius="lg" bg="purple.50" _dark={{ bg: 'whiteAlpha.100' }}>
                  <Icon as={MdCalculate} color="purple.500" w={5} h={5} />
                </Flex>
                <Box>
                  <Text fontSize="xs" color="gray.500">Promedio anual</Text>
                  <Text fontSize="lg" fontWeight="700">{stats.promedio.toFixed(1)}%</Text>
                </Box>
              </HStack>
            </CardBody>
          </Card>
          <Card variant="outline" bg={cardBg} borderColor={borderColor}>
            <CardBody py={4}>
              <HStack spacing={3}>
                <Flex align="center" justify="center" w="36px" h="36px" borderRadius="lg" bg="red.50" _dark={{ bg: 'whiteAlpha.100' }}>
                  <Icon as={MdTrendingUp} color="red.500" w={5} h={5} />
                </Flex>
                <Box overflow="hidden">
                  <Text fontSize="xs" color="gray.500">Se deprecia más rápido</Text>
                  <Text fontSize="sm" fontWeight="700" isTruncated title={stats.masAlto.Categoria}>
                    {stats.masAlto.Categoria} · {stats.masAlto.Porcentaje}%
                  </Text>
                </Box>
              </HStack>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      <Card variant="outline" bg={cardBg} borderColor={borderColor}>
        <CardBody>
          <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
            <Heading size="sm" fontWeight="600">Año</Heading>
            <Select
              maxW="160px"
              size="sm"
              value={anioSeleccionado}
              onChange={(e) => setAnioSeleccionado(Number(e.target.value))}
            >
              {anios.length === 0 && <option value={ANIO_ACTUAL}>{ANIO_ACTUAL}</option>}
              {anios.map(a => (
                <option key={a} value={a}>
                  {a}{a === ANIO_ACTUAL ? ' (actual)' : ''}
                </option>
              ))}
            </Select>
          </Flex>

          {cargando ? (
            <Flex justify="center" py={12}><Spinner /></Flex>
          ) : tabla.length === 0 && soloLectura ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
              No hay tabla de depreciación capturada para {anioSeleccionado}.
            </Text>
          ) : (
            <Box overflowX="auto">
              {tabla.length === 0 && (
                <Text fontSize="sm" color="gray.500" mb={3}>
                  No hay rubros capturados para {anioSeleccionado} todavía — agrega el primero abajo.
                </Text>
              )}
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Rubro / Categoría</Th>
                    <Th>Depreciación anual</Th>
                    <Th isNumeric>%</Th>
                    {!soloLectura && <Th></Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {tabla.map((fila) => {
                    const porcentaje = Number(fila.Porcentaje);
                    const colorScheme = getColorPorcentaje(porcentaje);
                    return (
                      <Tr key={fila.Id} _hover={{ bg: subtleBg }}>
                        <Td fontSize="sm">
                          <HStack spacing={2.5}>
                            <Icon as={getIconoCategoria(fila.Categoria)} color={`${colorScheme}.500`} w={4.5} h={4.5} />
                            {soloLectura ? (
                              <Text>{fila.Categoria}</Text>
                            ) : (
                              <Editable
                                defaultValue={fila.Categoria}
                                onSubmit={(val) => handleEditarCategoria(fila, val)}
                              >
                                <EditablePreview _hover={{ bg: subtleBg }} px={2} borderRadius="md" />
                                <EditableInput px={2} />
                              </Editable>
                            )}
                            {!!fila.ActivosEnUso && (
                              <Tooltip label={`${fila.ActivosEnUso} activo(s) usan este rubro`}>
                                <Badge colorScheme="gray" variant="subtle" fontSize="0.65em" borderRadius="full">
                                  {fila.ActivosEnUso}
                                </Badge>
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                        <Td w="140px">
                          <Progress
                            value={porcentaje}
                            max={PORCENTAJE_ESCALA_MAX}
                            size="xs"
                            colorScheme={colorScheme}
                            borderRadius="full"
                            bg={subtleBg}
                          />
                        </Td>
                        <Td isNumeric fontSize="sm">
                          {soloLectura ? (
                            <Badge colorScheme={colorScheme} variant="subtle" borderRadius="full" px={2}>
                              {fila.Porcentaje}%
                            </Badge>
                          ) : (
                            <Editable
                              defaultValue={String(fila.Porcentaje)}
                              onSubmit={(val) => handleEditarPorcentaje(fila, val)}
                              display="inline-flex"
                              justifyContent="flex-end"
                              alignItems="center"
                            >
                              <EditablePreview
                                fontWeight="600"
                                color={`${colorScheme}.600`}
                                _hover={{ bg: subtleBg }}
                                px={2}
                                borderRadius="md"
                              />
                              <EditableInput textAlign="right" w="70px" />
                            </Editable>
                          )}
                          {soloLectura ? '' : '%'}
                        </Td>
                        {!soloLectura && (
                          <Td>
                            <Tooltip
                              label={fila.ActivosEnUso ? `No se puede eliminar: ${fila.ActivosEnUso} activo(s) lo usan` : ''}
                              isDisabled={!fila.ActivosEnUso}
                            >
                              <IconButton
                                aria-label="Eliminar"
                                icon={<DeleteIcon />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                isDisabled={!!fila.ActivosEnUso}
                                onClick={() => handleEliminar(fila)}
                              />
                            </Tooltip>
                          </Td>
                        )}
                      </Tr>
                    );
                  })}
                  {!soloLectura && (
                    <Tr bg={subtleBg}>
                      <Td>
                        <HStack spacing={2.5}>
                          <Icon
                            as={getIconoCategoria(nuevaCategoria || '')}
                            color="gray.400"
                            w={4.5}
                            h={4.5}
                          />
                          <Input
                            size="sm"
                            variant="flushed"
                            placeholder="Nombre del nuevo rubro…"
                            value={nuevaCategoria}
                            onChange={(e) => setNuevaCategoria(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCrearCategoria()}
                          />
                        </HStack>
                      </Td>
                      <Td />
                      <Td isNumeric>
                        <NumberInput
                          size="sm"
                          min={0}
                          max={100}
                          value={nuevoPorcentaje}
                          onChange={(val) => setNuevoPorcentaje(val)}
                          w="80px"
                          ml="auto"
                        >
                          <NumberInputField
                            textAlign="right"
                            placeholder="%"
                            onKeyDown={(e) => e.key === 'Enter' && handleCrearCategoria()}
                          />
                        </NumberInput>
                      </Td>
                      <Td>
                        <IconButton
                          aria-label="Agregar rubro"
                          icon={<CheckIcon />}
                          size="xs"
                          colorScheme="blue"
                          isLoading={guardandoNueva}
                          isDisabled={!nuevaCategoria.trim() || !nuevoPorcentaje}
                          onClick={handleCrearCategoria}
                        />
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Modal: duplicar a nuevo año */}
      <Modal isOpen={isOpenDuplicar} onClose={onCloseDuplicar}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Duplicar tabla {anioSeleccionado} a un nuevo año</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.500" mb={4}>
              Copia los {tabla.length} rubros de {anioSeleccionado} hacia el año destino, para que solo
              tengas que ajustar los porcentajes que cambien.
            </Text>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Año destino</FormLabel>
              <NumberInput value={anioDestino} onChange={(val) => setAnioDestino(Number(val))}>
                <NumberInputField />
              </NumberInput>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCloseDuplicar}>Cancelar</Button>
            <Button colorScheme="blue" onClick={handleDuplicar} isLoading={duplicando}>
              Duplicar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
